import React from "react";
import { Alert } from 'react-bootstrap'
import QuoteStepBase from '../../components/QuoteStepBase'
import ButtonContinue from "../../components/ButtonContinue";
import VehicleCoverages from "./VehicleCoverages";
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import { QuoteStep } from "./QuoteStepLayout";
import { connect } from "react-redux";
import { PATHS, FULL_QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { IAppState } from "../../state/interfaces";
import { IVehicle, IBindQuoteResult, IExternalQuote } from "../../classes/ExternalQuote";
import Utilities from "../../classes/Utilities";
import { quoteStepChanged } from "../../state/appActions";
import { IPerson, ICoverageConfig, IPaymentChoice } from "../../classes/Domain";
import { bindQuote, reloadQuotePaymentInfo, IBindQuoteResponse, reSetQuoteEffDate } from "../../state/fullQuoteActions";
import Calendar from "../../components/Calendar";
import moment from 'moment';
import { validationUtils } from "../../validation/Validation";

const today = new Date();

interface IState extends IQuoteStepState {
    vehicleChanged: boolean,
    paymentChoice: IPaymentChoice | null,
    bindQuoteResult: IBindQuoteResult | null,
    externalQuote: IExternalQuote,
    rerating: boolean,
    effDateChanging: boolean
}

interface IProps extends IQuoteStepComponentProps {
    externalQuote: IExternalQuote,
    coverageConfig: ICoverageConfig,
    paymentChoice: IPaymentChoice,
    reloadQuotePaymentInfo: (quoteId: number) => Promise<any>,
    bindQuote: (quoteId: number) => Promise<any>,
    reSetQuoteEffDate: (quoteId: number, paymentPlanCode: number, effectiveDate: string) => Promise<any>;
}

class PolicyDetails extends QuoteStepBase<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.next = this.next.bind(this);
        this.stopUpdate = this.stopUpdate.bind(this);
        this.onEffDateChange = this.onEffDateChange.bind(this);

        this.state = {
            vehicleChanged: false,
            updatingStore: false,
            bindQuoteResult: null,
            errors: null,
            paymentChoice: null,
            externalQuote: this.props.externalQuote,
            rerating: false,
            effDateChanging: false
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        for (let i = 0; i < this.props.externalQuote.vehicles.length; i++) {
            if (this.props.externalQuote.vehicles[i].changed) {
                this.setState({ ...this.state, vehicleChanged: true });
                break;
            }
        }

        //console.log(this.props.reRated);

        //if (this.props.reRated) {
        //    this.props.reloadQuotePaymentInfo(this.props.quoteNumber);
        //}
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            
            return {
                ...prevState,
                paymentChoice: nextProps.paymentChoice
            };
        }

        return prevState;
    }

    stopUpdate() {
        this.setState({ ...this.state, updatingStore: false, rerating: false, effDateChanging: false });
    }

    onEffDateChange(date: Date | null) {
        if (date) {
            this.setState({ updatingStore: true, rerating: true, effDateChanging: false }, () => {
                this.props.reSetQuoteEffDate(this.props.externalQuote.rtrQuoteId, this.props.externalQuote.paymentPlanCode, date.toDateString())
                    .then(() => {
                        this.setState({
                            ...this.state,
                            updatingStore: false,
                            rerating: false,
                            effDateChanging: true
                        });
                    }, () => { this.stopUpdate(); });
            });
        }
    }

    showErrors(errors: Array<string> | undefined) {
        if (!errors) return null;
        return <Alert variant={"danger"}>
            <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
            <p>Please review errors below</p>
            <ul>
                {
                    errors.map((error) => (
                        <li key={Utilities.uniqueId()}>
                            <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                        </li>
                    ))
                }
            </ul>
        </Alert>;
    }

    next() {
        this.setState({ updatingStore: true, bindQuoteResult: null }, () => {
            if (this.props.externalQuote.hasMvrViolations || this.props.externalQuote.hasAdditionalDrivers) {
                this.props.bindQuote(this.props.externalQuote.rtrQuoteId)
                    .then((bindResponse: IBindQuoteResponse) => {
                        this.setState({ bindQuoteResult: bindResponse.data.bindQuoteResult }, () => {
                            if (null !== this.state.bindQuoteResult && this.state.bindQuoteResult.errors && 0 < this.state.bindQuoteResult.errors.length) {
                                this.stopUpdate();
                                return;
                            }
                            if (bindResponse.data.esignatureForms && 0 < bindResponse.data.esignatureForms.length) {
                                this.props.history.push(Utilities.formatUrl(PATHS.FQ_GOODTOGO, ""));
                            }
                            else {
                                this.props.history.push(PATHS.FQ_FINISH);
                            }
                        });
                    },
                    () => this.stopUpdate()
                );
            }
            else {
                this.props.history.push(PATHS.FQ_FINALIZE);
            }
        });
    }

    render() {
        return (
            <QuoteStep id="policyDetails" className="policy-details">
                <div className="titleArea">
                    <h5>Policy Details</h5>
                </div>

                {this.showErrors(this.state.bindQuoteResult?.errors)}

                {this.state.rerating
                    ?
                    <Alert variant={"warning"}>
                        Re-rating quote. Please wait...
                    </Alert>
                    : null
                }

                {this.state.vehicleChanged
                    ? <Alert variant={"warning"}>
                        Your vehicle information has changed based on the VIN provided. Please see adjusted premiums below.
                      </Alert>
                    : null
                }

                {this.state.effDateChanging
                    ? <Alert variant={"warning"}>
                        Your policy effective date has changed. Please see adjusted premiums below.
                      </Alert>
                    : null
                }


                <p className="info">Great; we are just about finished! Please review the details of the policy below: </p>

                <div className="payment-choice">
                    <div className="item">
                        <div className="eff-date-details">
                            <Calendar
                                id="effectiveDate"
                                value={new Date(this.state.externalQuote.effectiveDate)}
                                minDate={today}
                                maxDate={moment(new Date()).add(30, 'd').toDate()}
                                validated={null !== this.state.errors}
                                validationError={validationUtils.getValidationErrorFor(this.state.errors, "effectiveDate")}
                                onChange={this.onEffDateChange}
                                disabled={this.state.externalQuote.agreement?.agreePolicyEffDate} />
                        </div>
                        <label className="right-cell">Effective date</label>
                    </div>
                    <div className="item">
                        <label className="left-cell">{Utilities.formatCurrency(this.state.paymentChoice?.firstPay)}</label>
                        <label className="right-cell">Due today</label>
                    </div>
                    {this.state.externalQuote.installmentDueDate
                        ?
                            <div className="item">
                            <label className="left-cell">{Utilities.formatCurrency(this.state.paymentChoice?.regularPay)}</label>
                            <label className="right-cell">Per month starting on {this.state.externalQuote.installmentDueDate}</label>
                            </div>
                        : null
                        }
                </div>

                <div className="btnContainer">
                    <ButtonContinue
                        label={this.props.externalQuote.hasMvrViolations ? "Purchase Policy" : "Continue"}
                        className="btn-sign"
                        disabled={this.state.updatingStore}
                        showLoading={this.state.updatingStore}
                        onClick={this.next} />
                </div>

                <div className="info-disclosure quote-info">
                    <div className="item">
                        <label className="left-cell">Policy Coverage Start Date</label>
                        <label className="right-cell">{this.state.externalQuote.effectiveDate}</label>
                    </div>
                    <div className="item">
                        <label className="left-cell">Named Insured</label>
                        <label className="right-cell">{this.props.externalQuote.insured.firstName + " " + this.props.externalQuote.insured.lastName}</label>
                    </div>
                    <div className="item no-border">
                        <label className="left-cell">Package Name</label>
                        <label className="right-cell">{this.props.coverageConfig?.name}</label>
                    </div>
                </div>

                {this.props.externalQuote.vehicles.map((v) => (
                    <VehicleCoverages
                        key={Utilities.uniqueId()}
                        vehicle={v} />
                ))}

                <div className="btnContainer">
                    <ButtonContinue
                        label={this.props.externalQuote.hasMvrViolations ? "Purchase Policy" : "Continue"}
                        className="btn-sign"
                        disabled={this.state.updatingStore}
                        showLoading={this.state.updatingStore}
                        onClick={this.next} />
                </div>
                
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        coverageConfig: state.coverageConfig,
        paymentChoice: state.paymentChoice,
        externalQuote: state.externalQuote,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any) => {
    return {
        reportCurrentStep: () => dispatch(quoteStepChanged(FULL_QUOTE_STEPS.POLICY_DETAILS)),
        bindQuote: (quoteId: number) => dispatch(bindQuote(quoteId)),
        reloadQuotePaymentInfo: (quoteId: number) => dispatch(reloadQuotePaymentInfo(quoteId)),
        reSetQuoteEffDate: (quoteId: number, paymentPlanCode: number, effectiveDate: string) => dispatch(reSetQuoteEffDate(quoteId, paymentPlanCode, effectiveDate))
    };
};

export default connect(mapProps, mapDispatch)(PolicyDetails);
